import { useLocation, useNavigate } from "react-router-dom";
import { storageGet } from "utils/safeStorage";

/** How many history entries Back has to skip to leave this page: the booking steps (`?step=`) are entries on
 *  the car page's own route, so -1 from step 3 only reached step 2 (Faiza, 8 Oct). Reads the route history
 *  App.tsx keeps; 0 when it holds only this page (the renter landed here), 1 when there is no record. */
export const entriesToLeave = (pathname: string, history: string[]): number => {
  if (!history.length) return 1; // no record kept (storage blocked): plain one-step Back, as before
  let n = 0;
  for (let i = history.length - 1; i >= 0; i--) {
    if (history[i].split(/[?#]/)[0] !== pathname) return n || 1;
    n++;
  }
  return 0;
};

const routeHistory = (): string[] => {
  try {
    const parsed = JSON.parse(storageGet("session", "app_route_history") || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

/**
 * 1NOW-11228: what Back does. The page before this one when it is one of this site's, so the renter is back
 * where they were (home at the same scroll, or the search results with their filters, location and dates),
 * skipping the booking steps of this same page. Home when they landed here from a link, a bookmark or another
 * site: -1 would leave the site, or do nothing in a tab that opened on this page.
 */
export const useBackOrHome = () => {
  const navigate = useNavigate();
  const location = useLocation();
  return () => {
    // The router keys every entry it adds; the one the tab opened on is "default".
    let ours = location.key !== "default";
    // A full page load from one of our pages. Not a new tab opened from one: it has nothing to go back to.
    if (!ours && window.history.length > 1) {
      try {
        ours = new URL(document.referrer).origin === window.location.origin;
      } catch {
        ours = false;
      }
    }
    const back = ours ? entriesToLeave(location.pathname, routeHistory()) : 0;
    if (back > 0) navigate(-back);
    else navigate("/");
  };
};
